import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils';

const BookingModal = ({
    vendor,
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false,
    selectedPackage = null
}) => {
    const [bookingData, setBookingData] = useState({
        packageId: selectedPackage?.id || '',
        eventDate: '',
        eventTime: '',
        eventLocation: '',
        eventType: 'wedding',
        guestCount: '',
        totalAmount: selectedPackage?.price || 0,
        specialRequirements: '',
        contactName: '',
        contactEmail: '',
        contactPhone: ''
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (selectedPackage) {
            setBookingData(prev => ({
                ...prev,
                packageId: selectedPackage.id,
                totalAmount: selectedPackage.price
            }));
        }
    }, [selectedPackage]);

    if (!isOpen) return null;

    const eventTypes = [
        'Wedding',
        'Engagement',
        'Sangeet',
        'Mehndi',
        'Reception',
        'Pre-Wedding Shoot',
        'Other'
    ];

    const guestCountOptions = [
        '50-100',
        '100-200',
        '200-300',
        '300-500',
        '500+'
    ];

    const validateForm = () => {
        const newErrors = {};

        if (!bookingData.eventDate) newErrors.eventDate = 'Event date is required';
        if (!bookingData.eventLocation) newErrors.eventLocation = 'Event location is required';
        if (!bookingData.contactName) newErrors.contactName = 'Your name is required';
        if (!bookingData.contactEmail) newErrors.contactEmail = 'Email is required';
        if (!bookingData.contactPhone) newErrors.contactPhone = 'Phone number is required';

        // Validate email format
        if (bookingData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.contactEmail)) {
            newErrors.contactEmail = 'Invalid email format';
        }

        // Validate phone format (basic)
        if (bookingData.contactPhone && !/^\+?[\d\s\-()]+$/.test(bookingData.contactPhone)) {
            newErrors.contactPhone = 'Invalid phone number';
        }

        // Validate event date is in the future
        if (bookingData.eventDate && new Date(bookingData.eventDate) < new Date()) {
            newErrors.eventDate = 'Event date must be in the future';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            onSubmit(bookingData);
        }
    };

    const handleInputChange = (field, value) => {
        setBookingData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-border-light p-6 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-heading font-bold text-text-primary">
                            Book {vendor?.name}
                        </h2>
                        <p className="text-text-secondary text-sm mt-1">
                            Complete the form below to request a booking
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-smooth"
                        disabled={isSubmitting}
                    >
                        <Icon name="X" size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Package Selection */}
                    {vendor?.packages && vendor.packages.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Select Package {selectedPackage && '(Selected)'}
                            </label>
                            <select
                                value={bookingData.packageId}
                                onChange={(e) => {
                                    const pkg = vendor.packages.find(p => p.id === e.target.value);
                                    handleInputChange('packageId', e.target.value);
                                    handleInputChange('totalAmount', pkg?.price || 0);
                                }}
                                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                            >
                                <option value="">Choose a package (optional)</option>
                                {vendor.packages.map(pkg => (
                                    <option key={pkg.id} value={pkg.id}>
                                        {pkg.name} - {formatPrice(pkg.price)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Event Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Event Type <span className="text-error">*</span>
                            </label>
                            <select
                                value={bookingData.eventType}
                                onChange={(e) => handleInputChange('eventType', e.target.value)}
                                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                            >
                                {eventTypes.map(type => (
                                    <option key={type} value={type.toLowerCase()}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Event Date <span className="text-error">*</span>
                            </label>
                            <input
                                type="date"
                                value={bookingData.eventDate}
                                onChange={(e) => handleInputChange('eventDate', e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${errors.eventDate ? 'border-error' : 'border-border'
                                    }`}
                            />
                            {errors.eventDate && (
                                <p className="text-error text-sm mt-1">{errors.eventDate}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Event Time
                            </label>
                            <input
                                type="time"
                                value={bookingData.eventTime}
                                onChange={(e) => handleInputChange('eventTime', e.target.value)}
                                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-primary mb-2">
                                Guest Count
                            </label>
                            <select
                                value={bookingData.guestCount}
                                onChange={(e) => handleInputChange('guestCount', e.target.value)}
                                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                            >
                                <option value="">Select guest count</option>
                                {guestCountOptions.map(option => (
                                    <option key={option} value={option}>
                                        {option} guests
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Event Location */}
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                            Event Location <span className="text-error">*</span>
                        </label>
                        <input
                            type="text"
                            value={bookingData.eventLocation}
                            onChange={(e) => handleInputChange('eventLocation', e.target.value)}
                            placeholder="Enter venue name, city, or full address"
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${errors.eventLocation ? 'border-error' : 'border-border'
                                }`}
                        />
                        {errors.eventLocation && (
                            <p className="text-error text-sm mt-1">{errors.eventLocation}</p>
                        )}
                    </div>

                    {/* Contact Information */}
                    <div className="border-t border-border-light pt-6">
                        <h3 className="font-semibold text-lg mb-4">Your Contact Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-text-primary mb-2">
                                    Full Name <span className="text-error">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={bookingData.contactName}
                                    onChange={(e) => handleInputChange('contactName', e.target.value)}
                                    placeholder="Enter your full name"
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${errors.contactName ? 'border-error' : 'border-border'
                                        }`}
                                />
                                {errors.contactName && (
                                    <p className="text-error text-sm mt-1">{errors.contactName}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-primary mb-2">
                                    Email <span className="text-error">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={bookingData.contactEmail}
                                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                                    placeholder="your.email@example.com"
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${errors.contactEmail ? 'border-error' : 'border-border'
                                        }`}
                                />
                                {errors.contactEmail && (
                                    <p className="text-error text-sm mt-1">{errors.contactEmail}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-primary mb-2">
                                    Phone <span className="text-error">*</span>
                                </label>
                                <input
                                    type="tel"
                                    value={bookingData.contactPhone}
                                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                                    placeholder="(555) 123-4567"
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${errors.contactPhone ? 'border-error' : 'border-border'
                                        }`}
                                />
                                {errors.contactPhone && (
                                    <p className="text-error text-sm mt-1">{errors.contactPhone}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Special Requirements */}
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                            Special Requirements or Notes
                        </label>
                        <textarea
                            value={bookingData.specialRequirements}
                            onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
                            placeholder="Any specific requirements, preferences, or questions..."
                            rows={4}
                            className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth resize-vertical"
                            maxLength={500}
                        />
                        <p className="text-text-muted text-sm mt-1">
                            {bookingData.specialRequirements.length}/500 characters
                        </p>
                    </div>

                    {/* Total Amount */}
                    {bookingData.totalAmount > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-border-light">
                            <div className="flex items-center justify-between">
                                <span className="text-text-secondary">Estimated Total</span>
                                <span className="text-2xl font-bold text-primary">
                                    {formatPrice(bookingData.totalAmount)}
                                </span>
                            </div>
                            <p className="text-xs text-text-muted mt-2">
                                Final amount may vary based on specific requirements and vendor confirmation
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isSubmitting}
                            iconName={isSubmitting ? undefined : 'Calendar'}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Booking Request'
                            )}
                        </Button>
                    </div>

                    <p className="text-xs text-text-muted text-center">
                        By submitting this booking request, you agree to our terms and conditions.
                        The vendor will review your request and contact you within 24-48 hours.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default BookingModal;
